import { Monitor, Moon, Sun } from 'lucide-react';
import { useEffect, useState, type ComponentType, type FormEvent } from 'react';
import { Field, PageHeader } from '../components/page';
import { ActionError, QueryView } from '../components/states';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox, Input } from '../components/ui/input';
import { useApi, useAuth } from '../lib/auth';
import { cn } from '../lib/cn';
import { LANGUAGE_NAMES, PORTAL_LANGUAGES, useI18n } from '../lib/i18n';
import { usePreferences } from '../lib/preferences';
import { useApiMutation, useApiQuery } from '../lib/query';
import { THEMES, type ThemePreference } from '../lib/theme';
import type { Me } from '../lib/types';
import { SecuritySection } from './settings/security';

// Mon compte : préférences d'affichage (langue, thème, menu), nom, mot de passe et double
// authentification de l'utilisateur connecté. Les préférences suivent le compte d'un poste à l'autre.

const THEME_ICONS: Record<ThemePreference, ComponentType<{ className?: string }>> = {
  light: Sun,
  dark: Moon,
  system: Monitor,
};

/** Choix exclusif présenté en tuiles (langue, thème). */
function Choice<K extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: K;
  options: { key: K; label: string; icon?: ComponentType<{ className?: string }>; lang?: string }[];
  onChange: (key: K) => void;
}) {
  return (
    <fieldset>
      <legend className="mb-2 text-sm font-medium">{label}</legend>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        {options.map(({ key, label: text, icon: Icon, lang }) => (
          <label
            key={key}
            className={cn(
              'flex cursor-pointer items-center gap-3 rounded-xl border px-4 py-3 text-sm transition-colors',
              value === key
                ? 'border-primary bg-accent text-accent-foreground'
                : 'border-border hover:bg-muted',
            )}
          >
            <input
              type="radio"
              name={label}
              value={key}
              checked={value === key}
              onChange={() => onChange(key)}
              className="accent-primary"
            />
            {Icon ? <Icon className="h-4 w-4" aria-hidden /> : null}
            <span lang={lang}>{text}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

function PreferencesCard() {
  const t = useI18n().t;
  const { preferences, update } = usePreferences();
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t.account.display}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-5">
        <Choice
          label={t.common.language}
          value={preferences.language}
          options={PORTAL_LANGUAGES.map((l) => ({ key: l, label: LANGUAGE_NAMES[l], lang: l }))}
          onChange={(language) => void update({ language })}
        />
        <Choice
          label={t.theme.label}
          value={preferences.theme}
          options={THEMES.map((k) => ({ key: k, label: t.theme[k], icon: THEME_ICONS[k] }))}
          onChange={(theme) => void update({ theme })}
        />
        <label className="flex items-center gap-2 text-sm">
          <Checkbox
            checked={preferences.sidebarCollapsed}
            onChange={(e) => void update({ sidebarCollapsed: e.target.checked })}
          />
          {t.account.compactMenu}
        </label>
        <p className="text-xs text-muted-foreground">{t.account.savedOnAccount}</p>
      </CardContent>
    </Card>
  );
}

function ProfileCard({ me }: { me: Me }) {
  const t = useI18n().t;
  const [name, setName] = useState(me.name);
  const [saved, setSaved] = useState(false);
  useEffect(() => setName(me.name), [me.name]);
  const save = useApiMutation<{ name: string }, Me>('PATCH', '/v1/me', {
    invalidate: ['/v1/me'],
    onSuccess: () => setSaved(true),
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    if (name.trim()) save.mutate({ name: name.trim() });
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t.account.profile}</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={submit} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label={t.account.name}>
            <Input
              value={name}
              maxLength={120}
              onChange={(e) => {
                setSaved(false);
                setName(e.target.value);
              }}
            />
          </Field>
          <Field label={t.auth.email}>
            <Input value={me.email} readOnly dir="ltr" />
          </Field>
          <Field label={t.account.role}>
            <Input value={t.roles[me.role]} readOnly />
          </Field>
          <Field label={t.account.tenant}>
            <Input value={`${me.tenant.name} (${me.tenant.code})`} readOnly />
          </Field>
          <div className="flex items-center gap-3 sm:col-span-2">
            <Button
              type="submit"
              disabled={save.isPending || !name.trim() || name.trim() === me.name}
            >
              {t.common.save}
            </Button>
            {saved ? (
              <span role="status" className="text-sm text-success">
                {t.common.saved}
              </span>
            ) : null}
            <ActionError error={save.error} />
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

function PasswordCard() {
  const t = useI18n().t;
  const api = useApi();
  const [form, setForm] = useState({ current: '', next: '', confirm: '' });
  const [error, setError] = useState<Error | string | null>(null);
  const [done, setDone] = useState(false);
  const [pending, setPending] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setDone(false);
    if (form.next !== form.confirm) return setError(t.account.mismatch);
    setError(null);
    setPending(true);
    try {
      await api.request('POST', '/v1/me/password', {
        body: { currentPassword: form.current, newPassword: form.next },
      });
      setForm({ current: '', next: '', confirm: '' });
      setDone(true);
    } catch (err) {
      setError(err as Error);
    } finally {
      setPending(false);
    }
  }

  const set = (key: keyof typeof form) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t.account.password}</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={(e) => void submit(e)} className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Field label={t.account.currentPassword}>
            <Input
              type="password"
              autoComplete="current-password"
              value={form.current}
              onChange={set('current')}
              required
            />
          </Field>
          <Field label={t.account.newPassword} hint={t.account.passwordHint}>
            <Input
              type="password"
              autoComplete="new-password"
              value={form.next}
              onChange={set('next')}
              required
            />
          </Field>
          <Field label={t.account.confirmPassword}>
            <Input
              type="password"
              autoComplete="new-password"
              value={form.confirm}
              onChange={set('confirm')}
              required
            />
          </Field>
          <div className="flex flex-wrap items-center gap-3 sm:col-span-3">
            <Button type="submit" disabled={pending || !form.current || !form.next}>
              {t.account.changePassword}
            </Button>
            {done ? (
              <span role="status" className="text-sm text-success">
                {t.account.passwordChanged}
              </span>
            ) : null}
            {typeof error === 'string' ? (
              <p role="alert" className="text-sm text-destructive">
                {error}
              </p>
            ) : (
              <ActionError error={error} />
            )}
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

export function AccountPage() {
  const t = useI18n().t;
  const auth = useAuth();
  const me = useApiQuery<Me>(auth.state.user ? '/v1/me' : null);
  return (
    <>
      <PageHeader title={t.account.title} />
      <div className="grid gap-4 xl:grid-cols-2">
        <div className="flex flex-col gap-4">
          <PreferencesCard />
          <QueryView query={me}>{(data) => <ProfileCard me={data} />}</QueryView>
        </div>
        <div className="flex flex-col gap-4">
          <PasswordCard />
          <SecuritySection />
        </div>
      </div>
    </>
  );
}
